import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  DocumentTextIcon 
} from '@heroicons/react/24/outline';
import { getPublicForm, submitPublicForm } from '../services/formApi';
import { PublicForm as PublicFormType, FormField, FormSection, FormSubsection } from '../types/Form';
import AutoComplete from './AutoComplete';
import MultiSelectAutoComplete, { SelectedItem } from './MultiSelectAutoComplete';
import { educationUtils } from '../services/educationApi';
import logo from '../images/logo.png';

const PublicForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [form, setForm] = useState<PublicFormType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [organizationLogoFailed, setOrganizationLogoFailed] = useState(false);

  useEffect(() => {
    if (token) {
      loadForm();
      // Preload common education data for better performance
      educationUtils.preloadCommonData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadForm = async () => {
    try {
      setLoading(true);
      const response = await getPublicForm(token!);
      setForm(response.data);
      
      // Initialize form data with empty values
      const initialData: { [key: string]: any } = {};
      response.data.formStructure.sections.forEach(section => {
        if (section.fields) {
          section.fields.forEach(field => {
            initialData[field.name] = field.type === 'checkbox' ? [] : (field.type === 'select' || field.type === 'radio' ? '' : '');
          });
        }
        if (section.subsections) {
          section.subsections.forEach(subsection => {
            subsection.fields.forEach(field => {
              initialData[field.name] = field.type === 'checkbox' ? [] : (field.type === 'select' || field.type === 'radio' ? '' : '');
            });
          });
        }
      });
      setFormData(initialData);
      
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const validateField = (field: FormField, value: any): string | null => {
    const isEmpty = value === undefined || value === null ||
      (typeof value === 'string' && !value.trim()) ||
      (field.type === 'checkbox' && Array.isArray(value) && value.length === 0);

    // Required field validation
    if (field.required && isEmpty) {
      return `${field.label} is required`;
    }

    // Skip further validation if not required and empty
    if (isEmpty) {
      return null;
    }

    // Type-specific validation
    switch (field.type) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Please enter a valid email address';
        }
        break;

      case 'phone':
      case 'tel':
        const phoneRegex = /^[+]?[\s\-()]*([0-9][\s\-()]*){10,}$/;
        if (!phoneRegex.test(value)) {
          return 'Please enter a valid phone number';
        }
        break;

      case 'url':
        try {
          new URL(value);
        } catch {
          return 'Please enter a valid URL';
        }
        break;

      case 'number':
        if (isNaN(Number(value))) {
          return 'Please enter a valid number';
        }
        if (field.validation?.min !== undefined && Number(value) < field.validation.min) {
          return `Value must be at least ${field.validation.min}`;
        }
        if (field.validation?.max !== undefined && Number(value) > field.validation.max) {
          return `Value must be at most ${field.validation.max}`;
        }
        break;

      case 'text':
      case 'textarea':
        if (field.validation?.minLength && value.length < field.validation.minLength) {
          return `Must be at least ${field.validation.minLength} characters`;
        }
        if (field.validation?.maxLength && value.length > field.validation.maxLength) {
          return `Must be at most ${field.validation.maxLength} characters`;
        }
        break;

      case 'date': {
        const dateValue = new Date(value);
        if (isNaN(dateValue.getTime())) {
          return 'Please enter a valid date';
        }
        const year = dateValue.getFullYear();
        if (year < 1000 || year > 9999) {
          return 'Please enter a valid date with a 4-digit year (e.g. 2024)';
        }
        if (field.validation?.minDate) {
          const minDate = new Date(field.validation.minDate);
          if (dateValue < minDate) {
            return `Date must be after ${minDate.toLocaleDateString()}`;
          }
        }
        if (field.validation?.maxDate) {
          const maxDate = new Date(field.validation.maxDate);
          if (dateValue > maxDate) {
            return `Date must be before ${maxDate.toLocaleDateString()}`;
          }
        }
        break;
      }
    }

    // Custom validation pattern
    if (field.validation?.pattern) {
      const regex = new RegExp(field.validation.pattern);
      if (!regex.test(value)) {
        return field.validation.patternMessage || 'Invalid format';
      }
    }

    return null;
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    
    if (!form) return false;
    
    // Validate all fields using enhanced validation
    form.formStructure.sections.forEach(section => {
      const checkFields = (fields: FormField[]) => {
        fields.forEach(field => {
          const error = validateField(field, formData[field.name]);
          if (error) {
            errors[field.name] = error;
          }
        });
      };
      
      if (section.fields) {
        checkFields(section.fields);
      }
      
      if (section.subsections) {
        section.subsections.forEach(subsection => {
          checkFields(subsection.fields);
        });
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Transform multiselect data for submission
      const transformedData = { ...formData };
      
      // Convert SelectedItem arrays to appropriate format for backend
      Object.keys(transformedData).forEach(key => {
        const value = transformedData[key];
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'name' in value[0]) {
          // This is a SelectedItem array, convert to comma-separated names
          transformedData[key] = value.map((item: SelectedItem) => item.name).join(', ');
        }
      });
      
      // Extract submitter info from form data
      const submitterName = transformedData.first_name && transformedData.last_name 
        ? `${transformedData.first_name} ${transformedData.last_name}`
        : '';
      
      await submitPublicForm(token!, {
        submissionData: transformedData,
        submitterEmail: transformedData.email || '',
        submitterName
      });
      
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.name] || '';
    const hasError = !!validationErrors[field.name];
    
    const baseClasses = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
      hasError ? 'border-red-300' : 'border-gray-300'
    }`;

    // Check if this field should use AutoComplete
    const isInstituteField = field.name.includes('institute') || 
                           field.name.includes('school') || 
                           field.name.includes('college') || 
                           field.name.includes('university');
    
    const isCountryField = field.name.includes('country');
    const isMultiSelectField = field.type === 'multiselect';
    
    // Handle MultiSelect fields (new enhanced fields)
    if (isMultiSelectField) {
      const selectedItems: SelectedItem[] = Array.isArray(value) ? value : [];
      
      // Get filter values for dependent fields
      let filterCountry: string | undefined;
      let filterCity: string | undefined;
      
      if (field.name === 'preferred_cities' && formData['preferred_countries']) {
        const countries = Array.isArray(formData['preferred_countries']) ? formData['preferred_countries'] : [];
        filterCountry = countries.length > 0 ? countries[0].name : undefined;
      }
      
      if (field.subtype === 'university') {
        const countries = Array.isArray(formData['preferred_countries']) ? formData['preferred_countries'] : [];
        const cities = Array.isArray(formData['preferred_cities']) ? formData['preferred_cities'] : [];
        filterCountry = countries.length > 0 ? countries[0].name : undefined;
        filterCity = cities.length > 0 ? cities[0].name : undefined;
      }
      
      return (
        <div key={field.name} className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <MultiSelectAutoComplete
            type={field.subtype as 'university' | 'country' | 'city'}
            values={selectedItems}
            onChange={(newValues) => handleInputChange(field.name, newValues)}
            placeholder={field.placeholder || `Select ${field.label.toLowerCase()}...`}
            filterCountry={filterCountry}
            filterCity={filterCity}
            allowCustom={field.allowCustom !== false}
            allowCrossLocation={field.allowCrossLocation !== false}
            required={field.required}
            error={hasError ? validationErrors[field.name] : undefined}
            className="w-full"
          />
          {field.subtype === 'university' && (
            <p className="text-xs text-gray-500 mt-1">
              💡 You can select universities from any country or add custom ones not in our database
            </p>
          )}
        </div>
      );
    }
    
    // Use AutoComplete for institute and country fields only when they are free-text (no options).
    // Option-based fields (select, radio, checkbox) with their own options must render those options.
    const hasOwnOptions = field.options && field.options.length > 0;
    if (isInstituteField && !hasOwnOptions) {
      const countryField = formData['country'] || formData['preferred_countries'] || undefined;
      
      return (
        <div key={field.name} className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <AutoComplete
            type="institution"
            value={value}
            onChange={(newValue) => handleInputChange(field.name, newValue)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            country={countryField}
            required={field.required}
            error={hasError ? validationErrors[field.name] : undefined}
            className="w-full"
          />
        </div>
      );
    }
    
    if (isCountryField && !hasOwnOptions) {
      return (
        <div key={field.name} className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <AutoComplete
            type="country"
            value={value}
            onChange={(newValue) => handleInputChange(field.name, newValue)}
            placeholder={field.placeholder || `Select ${field.label.toLowerCase()}...`}
            required={field.required}
            error={hasError ? validationErrors[field.name] : undefined}
            className="w-full"
          />
        </div>
      );
    }

    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
              className={baseClasses}
            />
            {hasError && (
              <p className="text-red-500 text-xs">{validationErrors[field.name]}</p>
            )}
          </div>
        );
        
      case 'select':
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              className={baseClasses}
            >
              <option value="">Select {field.label}</option>
              {(field.options || []).map(option => (
                <option key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </option>
              ))}
            </select>
            {hasError && (
              <p className="text-red-500 text-xs">{validationErrors[field.name]}</p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {(field.options || []).map((option) => (
                <div key={option} className="flex items-center">
                  <input
                    type="radio"
                    id={`${field.name}-${option}`}
                    name={field.name}
                    value={option}
                    checked={value === option}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor={`${field.name}-${option}`} className="ml-2 text-sm text-gray-700">
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                </div>
              ))}
            </div>
            {hasError && (
              <p className="text-red-500 text-xs">{validationErrors[field.name]}</p>
            )}
          </div>
        );

      case 'checkbox':
        const selectedCheckboxes = Array.isArray(value) ? value : [];
        const handleCheckboxChange = (option: string, checked: boolean) => {
          const next = checked
            ? [...selectedCheckboxes, option]
            : selectedCheckboxes.filter((v) => v !== option);
          handleInputChange(field.name, next);
        };
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {(field.options || []).map((option) => (
                <div key={option} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`${field.name}-${option}`}
                    value={option}
                    checked={selectedCheckboxes.includes(option)}
                    onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor={`${field.name}-${option}`} className="ml-2 text-sm text-gray-700">
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                </div>
              ))}
            </div>
            {hasError && (
              <p className="text-red-500 text-xs">{validationErrors[field.name]}</p>
            )}
          </div>
        );

      case 'date': {
        // Restrict to 4-digit year: use YYYY-MM-DD and min/max so native picker doesn't allow 6-digit years
        const toYYYYMMDD = (val: any): string => {
          if (val === undefined || val === null || val === '') return '';
          const d = new Date(val);
          if (isNaN(d.getTime())) return '';
          const y = d.getFullYear();
          if (y < 1000 || y > 9999) return ''; // invalid year, show empty
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        const dateInputValue = toYYYYMMDD(value);
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={dateInputValue}
              min="1900-01-01"
              max="9999-12-31"
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              className={baseClasses}
            />
            {hasError && (
              <p className="text-red-500 text-xs">{validationErrors[field.name]}</p>
            )}
          </div>
        );
      }
        
      default:
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type={field.type}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              className={baseClasses}
            />
            {hasError && (
              <p className="text-red-500 text-xs">{validationErrors[field.name]}</p>
            )}
          </div>
        );
    }
  };

  const renderSection = (section: FormSection) => {
    return (
      <div key={section.title} className="p-4 sm:p-6 mb-4 sm:mb-6 border-b border-gray-100 last:border-b-0">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b border-gray-200">
          {section.title}
        </h2>
        
        {/* Direct fields in section */}
        {section.fields && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {section.fields.map(renderField)}
          </div>
        )}
        
        {/* Subsections */}
        {section.subsections?.map((subsection: FormSubsection) => (
          <div key={subsection.title} className="mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-2 sm:mb-3 border-l-4 border-blue-500 pl-3">
              {subsection.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subsection.fields.map(renderField)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mr-4" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Form Not Found</h1>
              <p className="text-gray-600">{error}</p>
            </div>
          </div>
          <p className="text-gray-500 text-sm">
            The form you're looking for may have been removed or the link may be invalid.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const hasBanner = !!form!.bannerUrl;
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <header className="relative w-full max-w-full min-w-0 rounded-xl overflow-hidden mb-6 sm:mb-8 shadow-sm border border-gray-200 aspect-[3/1] min-h-[200px] sm:min-h-[240px] max-h-[320px] flex flex-col justify-center">
            {hasBanner ? (
              <>
                <img
                  src={form!.bannerUrl!}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/50 to-black/75" aria-hidden="true" />
              </>
            ) : (
              <div className="absolute inset-0 bg-gray-100" aria-hidden="true" />
            )}
            <div className={`relative z-10 px-4 py-5 sm:py-6 sm:px-6 text-center ${hasBanner ? 'text-white' : 'text-gray-900'}`}>
              <h1 className={`text-lg sm:text-xl lg:text-2xl font-bold tracking-tight ${hasBanner ? 'text-white drop-shadow-sm' : 'text-gray-900'}`}>{form!.name}</h1>
              <div className={`flex items-center justify-center gap-1.5 flex-wrap text-xs sm:text-sm mt-1.5 ${hasBanner ? 'text-white/90' : 'text-gray-500'}`}>
                <span>Organized by</span>
                <span className={hasBanner ? 'text-white/70' : 'text-gray-400'}>·</span>
                <span className={hasBanner ? 'font-medium text-white' : 'font-medium text-gray-700'}>{form!.organizationName}</span>
              </div>
            </div>
          </header>
          <div className="text-center bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6">
              <CheckCircleIcon className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 tracking-tight">Application Submitted Successfully!</h2>
            <p className="text-base sm:text-lg text-gray-600 mb-5 sm:mb-6 max-w-xl mx-auto">
              Your application has been submitted successfully. We will review your information and get back to you soon.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6 max-w-xl mx-auto text-left">
              <p className="text-blue-800 text-sm sm:text-base">
                <strong>What&apos;s next?</strong><br />
                Our team will review your application and contact you within 2-3 business days.
              </p>
            </div>
          </div>
          <footer className="text-center pt-6 border-t border-gray-200 mt-8">
            <div className="flex items-center justify-center gap-2 text-gray-500 text-xs sm:text-sm mb-1">
              <span>Powered by</span>
              <img src={logo} alt="LeadsHive" className="h-8 sm:h-10 w-auto" />
            </div>
            <p className="text-gray-400 text-xs">© {new Date().getFullYear()} {form!.organizationName}. All rights reserved.</p>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <header className="relative w-full max-w-full min-w-0 rounded-xl overflow-hidden mb-5 sm:mb-6 shadow-sm border border-gray-200 aspect-[3/1] min-h-[200px] sm:min-h-[240px] max-h-[320px] flex flex-col justify-center">
          {form!.bannerUrl ? (
            <>
              <img
                src={form!.bannerUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/50 to-black/75" aria-hidden="true" />
            </>
          ) : (
            <div className="absolute inset-0 bg-gray-100" aria-hidden="true" />
          )}
          <div className={`relative z-10 px-4 py-5 sm:py-6 sm:px-6 text-left ${form!.bannerUrl ? 'text-white' : 'text-gray-900'}`}>
            <div className="max-w-2xl">
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight mb-1.5 sm:mb-2 ${form!.bannerUrl ? 'text-white drop-shadow-sm' : 'text-gray-900'}`}>
                {form!.name}
              </h1>
              {form!.description && (
                <p className={`text-sm sm:text-base leading-relaxed mb-1.5 sm:mb-2 ${form!.bannerUrl ? 'text-white/95' : 'text-gray-600'}`}>
                  {form!.description}
                </p>
              )}
              <p className={`text-xs sm:text-sm italic mt-1.5 sm:mt-2 text-left ${form!.bannerUrl ? 'text-white/90' : 'text-gray-500'}`}>
                by {form!.organizationName}
              </p>
            </div>
          </div>
        </header>

        {!form!.allowMultipleSubmissions && (
          <div className="mb-5 sm:mb-6 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm font-medium"><strong>Note:</strong> You can only submit this form once. Please review all information carefully before submitting.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-5 sm:mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <form onSubmit={handleSubmit}>
            {form!.formStructure.sections.map(renderSection)}
            <div className="bg-gray-50/80 px-4 py-5 sm:px-6 sm:py-6 border-t border-gray-200">
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full sm:w-auto sm:min-w-[200px] px-6 py-3.5 sm:py-4 rounded-xl font-semibold text-base sm:text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 ${submitting ? 'bg-gray-400 text-gray-700 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'}`}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      Submitting...
                    </span>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <footer className="mt-8 sm:mt-10 pt-6 text-center border-t border-gray-200">
          <div className="flex items-center justify-center gap-2 text-gray-500 text-xs sm:text-sm mb-1">
            <span>Powered by</span>
            <img src={logo} alt="LeadsHive" className="h-8 sm:h-10 w-auto" />
          </div>
          <p className="text-gray-400 text-xs mt-1">© {new Date().getFullYear()} {form!.organizationName}. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default PublicForm;
